import { observable, action, runInAction, makeObservable, computed } from 'mobx'
import PolicyService from 'services/PolicyService'
import QuoteCreationService from 'services/QuoteCreationService'
import PolicyCancellationReasonsDto from 'dto/PolicyCancellationReasonsDto'
import PolicyModifyDto from 'dto/PolicyModifyDto'
import Paginator from 'models/Paginator'
import Sorter from 'models/Sorter'
import SilentListStore from './SilentListStore'

export default class PolicyStore extends SilentListStore {
    DEFAULT_SORT_FIELD = 'validFrom'
    DEFAULT_SORT_SENSE = 'DESC'
    policies = []
    pages = ''
    selectedPolicy = {}
    policyStateFilterSelected = 'actives'
    policyStatuses = ['activated']
    ending = false
    currentPage = 1

    isLoadingPolicyDetails = true
    isLoadingPolicyEndorsements = true
    isLoadingpolicyAmendmentPetitions = true

    policyEndorsements = []
    policyAmendmentDetailList = []
    policyAmendmentDetail = {}
    policyAmendmentPetitions = []
    policyAmendmentPetitionDetail = {}
    cancellationReasons = []

    paginator = Paginator.empty()
    sorter = Sorter.fromJson({
        sortField: this.DEFAULT_SORT_FIELD,
        sortSense: this.DEFAULT_SORT_SENSE,
    })

    filterSelectedCompany = null
    filterSelectedOrigin = null
    filterSelectedBroker = null
    optionTypeSelected = null
    nameTextSearch = null
    excludeRenewals = false

    insuranceCompanies = []
    typeOptions = []

    constructor() {
        super({ itemsKey: 'policies', cacheExtraKeys: ['pages'] })

        this.makeObservables()
        this.policyService = new PolicyService()
        this.quoteCreationService = new QuoteCreationService()
    }

    setDefaultSort() {
        this.setSort(this.DEFAULT_SORT_FIELD, this.DEFAULT_SORT_SENSE)
    }

    setSelectedAmendmentId(id) {
        this.selectedAmendmentId = id
    }

    changePage(page) {
        this.currentPage = page
    }

    changeSort(sortField) {
        this.sorter.changeSort(sortField)
    }

    setSort(sortField, sortSense) {
        if (sortField && sortSense) {
            this.sorter.setSort(sortField, sortSense)
        } else {
            this.setDefaultSort()
        }
    }

    getListCacheKey() {
        return [
            this.policyStateFilterSelected || '',
            this.nameTextSearch || '',
            this.filterSelectedCompany || '',
            this.filterSelectedOrigin || '',
            this.filterSelectedBroker || '',
            this.optionTypeSelected || '',
            this.excludeRenewals ? 'excludeRenewals' : '',
            this.currentPage,
            this.sorter.sortField,
            this.sorter.sortSense,
            this.ending ? 'ending' : '',
        ].join('|')
    }

    async listPolicies({ silent = false } = {}) {
        await this.fetchList({
            silent,
            onRetry: () => this.listPolicies(),
            request: async () => {
                let { sortField } = this.sorter

                if (sortField === 'displayName') {
                    sortField = 'insured'
                }

                const params = {
                    page: this.currentPage,
                    policyStatuses: this.policyStatuses,
                    search: this.nameTextSearch,
                    ending: this.ending,
                    [`sort[${sortField}]`]: this.sorter.sortSense,
                    insuranceCompany: this.filterSelectedCompany ? this.filterSelectedCompany : null,
                    group: this.filterSelectedOrigin ? this.filterSelectedOrigin : null,
                    broker: this.filterSelectedBroker ? this.filterSelectedBroker : null,
                    type: this.optionTypeSelected ? this.optionTypeSelected : null,
                }

                if (this.filterSelectedCompany) {
                    params.insuranceCompany = this.filterSelectedCompany
                }

                if (this.filterSelectedOrigin) {
                    params.group = this.filterSelectedOrigin
                }

                if (this.filterSelectedBroker) {
                    params.broker = this.filterSelectedBroker
                }

                if (this.optionTypeSelected) {
                    params.type = this.optionTypeSelected
                }

                if (this.excludeRenewals) {
                    params.excludeRenewals = true
                }

                const response = await this.policyService.listPolicies(params)

                return {
                    items: response.policies,
                    paginator: response.paginator,
                    pages: response.pagination?.pages ?? '',
                }
            },
        })
    }

    get hasDocuments() {
        return (
            Array.isArray(this.selectedPolicy.downloadDocuments) &&
            this.selectedPolicy.downloadDocuments.length > 0
        )
    }

    async showPolicy(id) {
        this.isLoadingPolicyDetails = true
        this.isLoadingPolicyEndorsements = true
        this.preRequest(this.showPolicy)

        try {
            const policy = await this.policyService.showPolicy(id)
            policy.editableData = PolicyModifyDto.fromJson(policy)

            runInAction(() => {
                this.selectedPolicy = policy
                this.isLoadingPolicyDetails = false
                this.isLoadingPolicyEndorsements = false
                this.onSuccessRequest()
            })
        } catch (e) {
            runInAction(() => {
                this.isLoadingPolicyDetails = false
                this.isLoadingPolicyEndorsements = false

                this.onErrorRequest(e)
            })
        }
    }

    async deletePolicy(id) {
        this.preRequest(this.deletePolicy)

        try {
            await this.policyService.deletePolicy(id)

            runInAction(() => {
                this.onSuccessRequest()
            })
        } catch (e) {
            runInAction(() => {
                this.onErrorRequest(e)
            })
        }
    }

    async createMultiQuoteFromPolicy(policyId, localityId, commissionType) {
        const response = await this.policyService.createMultiQuoteFromPolicy(policyId, localityId, commissionType)
        return response.data
    }

    async listPolicyAmendment(id) {
        this.preRequest(this.listPolicies)
        this.isLoadingPolicyEndorsements = true

        try {
            const response = await this.policyService.listPolicyAmendment(id)
            runInAction(() => {
                this.policyEndorsements = response
                this.isLoadingPolicyEndorsements = false

                this.onSuccessRequest()
            })
        } catch (e) {
            runInAction(() => {
                this.isLoadingPolicyEndorsements = false

                this.onErrorRequest(e)
            })
        }
    }

    async getPolicyAmendmentDetails(endorsementId, amendmentId) {
        this.preRequest(this.getPolicyAmendmentDetails)
        this.isLoadingPolicyEndorsements = true

        try {
            const response = await this.policyService.getPolicyAmendmentDetails(
                endorsementId,
                amendmentId
            )

            runInAction(() => {
                this.policyAmendmentDetail = response
                this.isLoadingPolicyEndorsements = false

                this.onSuccessRequest()
            })
        } catch (e) {
            runInAction(() => {
                this.isLoadingPolicyEndorsements = false

                this.onErrorRequest(e)
            })
        }
    }

    async listPolicyAmendmentPetitions(id) {
        this.preRequest(this.listPolicyAmendmentPetitions)
        this.isLoadingpolicyAmendmentPetitions = true

        try {
            const response = await this.policyService.listPolicyAmendmentPetitions(id)
            runInAction(() => {
                this.policyAmendmentPetitions = response.policyAmendmentPetitions
                this.paginator = response.paginator
                this.pages = response.pagination.pages
                this.isLoadingpolicyAmendmentPetitions = false
                this.onSuccessRequest()
            })
        } catch (e) {
            runInAction(() => {
                this.isLoadingpolicyAmendmentPetitions = false

                this.onErrorRequest(e)
            })
        }
    }

    setpolicyAmendmentPetitionDetail(policyAmendmentPetitionDetail) {
        this.policyAmendmentPetitionDetail = policyAmendmentPetitionDetail
    }

    async createPolicyAmendmentPetition(endorsementId, data) {
        this.isLoadingpolicyAmendmentPetitions = true
        this.preRequest(this.createPolicyAmendmentPetition)

        try {
            const response = await this.policyService.createPolicyAmendmentPetition(
                endorsementId,
                data
            )
            runInAction(() => {
                this.policyAmendmentPetitionDetail = response
                this.isLoadingpolicyAmendmentPetitions = false
                this.onSuccessRequest()
            })
        } catch (e) {
            runInAction(() => {
                this.isLoadingpolicyAmendmentPetitions = false

                this.onErrorRequest(e)
            })
        }
    }

    get hasUserDataError() {
        return this.name.error || this.lastName.error || this.email.error || this.password.error
    }

    filterPoliciesByState = (policyState) => {
        switch (policyState) {
            case 'emission':
                this.policyStateFilterSelected = 'emission'
                this.policyStatuses = ['in_emission']
                this.ending = false
                break
            case 'toBeat':
                this.policyStateFilterSelected = 'toBeat'
                this.policyStatuses = ['activated']
                this.ending = true
                break
            case 'all':
                this.policyStateFilterSelected = 'all'
                this.policyStatuses = []
                this.ending = false
                break
            case 'actives':
            default:
                this.policyStateFilterSelected = 'actives'
                this.policyStatuses = ['activated']
                this.ending = false
                break
        }
    }

    filterPoliciesByName = (nameTextSearch) => {
        this.nameTextSearch = nameTextSearch
    }

    async loadInsuranceCompanies() {
        try {
            const response = await this.policyService.getInsuranceCompanies()

            runInAction(() => {
                this.insuranceCompanies = response.map(({ name: id, displayName: value }) => ({
                    id,
                    value,
                }))
            })
        } catch (e) {
            runInAction(() => {
                this.onErrorRequest(e)
            })
        }
    }

    async loadInsuranceTypes() {
        try {
            const response = await this.policyService.getInsuranceTypes()

            runInAction(() => {
                this.typeOptions = response.map(({ name: id, displayName: value }) => ({
                    id,
                    value,
                }))
            })
        } catch (e) {
            runInAction(() => {
                this.onErrorRequest(e)
            })
        }
    }

    filterPoliciesByCompany(company) {
        this.filterSelectedCompany = company
    }

    filterPoliciesByOrigin(group) {
        this.filterSelectedOrigin = group
    }

    filterPoliciesByBroker(broker) {
        this.filterSelectedBroker = broker
    }

    filterPoliciesByType(optionStatus) {
        this.optionTypeSelected = optionStatus
    }

    filterPoliciesByExcludeRenewals(excludeRenewals) {
        this.excludeRenewals = excludeRenewals === 'true'
    }

    async loadCancellationReasons() {
        this.preRequest(this.loadCancellationReasons)
        this.clearError()

        try {
            const response = await this.policyService.listPolicyAmendmentsCancellationReasons()

            runInAction(() => {
                this.cancellationReasons = PolicyCancellationReasonsDto.fromJson(response)

                this.onSuccessRequest()
            })
        } catch (e) {
            runInAction(() => {
                this.onErrorRequest(e)
            })
        }
    }

    makeObservables() {
        makeObservable(this, {
            // observables
            cancellationReasons: observable,
            currentPage: observable,
            filterSelectedCompany: observable,
            filterSelectedOrigin: observable,
            filterSelectedBroker: observable,
            insuranceCompanies: observable,
            isLoadingpolicyAmendmentPetitions: observable,
            isLoadingPolicyDetails: observable,
            isLoadingPolicyEndorsements: observable,
            nameTextSearch: observable,
            optionTypeSelected: observable,
            excludeRenewals: observable,
            pages: observable,
            paginator: observable,
            policies: observable,
            policyAmendmentDetail: observable,
            policyAmendmentDetailList: observable,
            policyAmendmentPetitionDetail: observable,
            policyAmendmentPetitions: observable,
            policyEndorsements: observable,
            policyStateFilterSelected: observable,
            selectedPolicy: observable,
            setDefaultSort: observable,
            sorter: observable,
            typeOptions: observable,
            // actions
            changePage: action,
            changeSort: action,
            deletePolicy: action,
            filterPoliciesByCompany: action,
            filterPoliciesByOrigin: action,
            filterPoliciesByBroker: action,
            filterPoliciesByName: action,
            filterPoliciesByState: action,
            filterPoliciesByType: action,
            filterPoliciesByExcludeRenewals: action,
            getPolicyAmendmentDetails: action,
            listPolicies: action,
            listPolicyAmendment: action,
            listPolicyAmendmentPetitions: action,
            loadCancellationReasons: action,
            loadInsuranceCompanies: action,
            loadInsuranceTypes: action,
            setpolicyAmendmentPetitionDetail: action,
            setSelectedAmendmentId: action,
            setSort: action,
            showPolicy: action,
            // computed
            hasDocuments: computed,
            hasUserDataError: computed,
            ...this.getSilentListAnnotations(),
        })
    }
}
